import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import {
  GALLERY_CATEGORIES,
  STATUS_OPTIONS,
} from "../types/publicGalleryTypes";

const GalleryForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    eventId: "",
    name: "",
    description: "",
    category: "wedding",
    status: "draft",
    isPublic: false,
    displayOrder: 0,
    tags: [],
    settings: {
      allowDownload: true,
      allowComments: false,
      allowShare: true,
      showWatermark: false,
    },
  });

  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        eventId: initialData.eventId || "",
        name: initialData.name || "",
        description: initialData.description || "",
        category: initialData.category || "wedding",
        status: initialData.status || "draft",
        isPublic: initialData.isPublic ?? false,
        displayOrder: initialData.displayOrder || 0,
        tags: initialData.tags || [],
        settings: {
          allowDownload: initialData.settings?.allowDownload ?? true,
          allowComments: initialData.settings?.allowComments ?? false,
          allowShare: initialData.settings?.allowShare ?? true,
          showWatermark: initialData.settings?.showWatermark ?? false,
        },
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("settings.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [key]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Gallery name is required";
    if (!formData.category) newErrors.category = "Category is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Gallery Name *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
          disabled={loading}
          placeholder="e.g., Akash & Priya Wedding"
        />

        <TextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={3}
          fullWidth
          disabled={loading}
          placeholder="Wedding Highlights"
        />

        <TextField
          label="Linked Event ID"
          name="eventId"
          value={formData.eventId}
          onChange={handleChange}
          fullWidth
          disabled={loading}
          placeholder="EVT-000001"
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.category}>
              <InputLabel>Category *</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category *"
                disabled={loading}
              >
                {GALLERY_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
                disabled={loading}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TextField
          label="Display Order"
          name="displayOrder"
          type="number"
          value={formData.displayOrder}
          onChange={handleChange}
          fullWidth
          disabled={loading}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.isPublic}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, isPublic: e.target.checked }))
              }
              disabled={loading}
            />
          }
          label="Make gallery publicly visible"
        />

        <Divider>
          <Typography variant="caption">Tags</Typography>
        </Divider>

        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
          >
            {formData.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                disabled={loading}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Add tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => e.key === "Enter" && handleAddTag(e)}
            />
            <Button
              variant="outlined"
              onClick={handleAddTag}
              disabled={loading}
            >
              Add
            </Button>
          </Stack>
        </Box>

        <Divider>
          <Typography variant="caption">Gallery Settings</Typography>
        </Divider>

        <Grid container>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.allowDownload}
                  onChange={handleChange}
                  name="settings.allowDownload"
                  disabled={loading}
                />
              }
              label="Allow Download"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.allowComments}
                  onChange={handleChange}
                  name="settings.allowComments"
                  disabled={loading}
                />
              }
              label="Allow Comments"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.allowShare}
                  onChange={handleChange}
                  name="settings.allowShare"
                  disabled={loading}
                />
              }
              label="Allow Share"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.showWatermark}
                  onChange={handleChange}
                  name="settings.showWatermark"
                  disabled={loading}
                />
              }
              label="Show Watermark"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Gallery"
                : "Create Gallery"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default GalleryForm;
